// Componentes do design system do Polaris, compartilhados entre console e portal.
// Acessibilidade (RNF-ADM-05): rótulos sempre ligados aos campos, foco preso em diálogos,
// navegação por teclado em abas e árvore, mensagens em aria-live e nunca cor sozinha.
import React, { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { CircleHelp } from "lucide-react";
import { Box } from "./components/ui/box";
import { buttonVariants } from "./components/ui/button";
import { Checkbox } from "./components/ui/checkbox";
import { Dialog } from "./components/ui/dialog";
import { cn } from "./lib/utils";

export { Dialog, Checkbox, Box };
export { Input } from "./components/ui/input";
export { Textarea } from "./components/ui/textarea";
export { Select } from "./components/ui/select";

type Children = { children?: React.ReactNode };

const buttonVariant = (variant?: "primary" | "danger" | "ghost") =>
  variant === "primary" || variant === "danger" || variant === "ghost" ? variant : "outline";

export function Button({ variant, small, loading, className, children, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "danger" | "ghost"; small?: boolean; loading?: boolean;
}) {
  return (
    <button
      {...rest}
      type={rest.type ?? "button"}
      className={cn(buttonVariants({ variant: buttonVariant(variant), size: small ? "sm" : "default" }), className)}
      disabled={loading || rest.disabled}
      aria-busy={loading || undefined}
    >
      {loading && <span className="spinner" aria-hidden="true" />}
      {children}
    </button>
  );
}

export function Card({ title, actions, children, className }: Children & { title?: React.ReactNode; actions?: React.ReactNode; className?: string }) {
  return (
    <Box className={cn("card", className)}>
      {(title || actions) && (
        <div className="row" style={{ marginBottom: 10 }}>
          {title && <h2 style={{ margin: 0 }}>{title}</h2>}
          {actions && <div className="row" style={{ marginLeft: "auto" }}>{actions}</div>}
        </div>
      )}
      {children}
    </Box>
  );
}

export function PageHead({ title, subtitle, actions, badges }: { title: React.ReactNode; subtitle?: React.ReactNode; actions?: React.ReactNode; badges?: React.ReactNode }) {
  return (
    <header className="page-head">
      <div>
        <div className="row"><h1>{title}</h1>{badges}</div>
        {subtitle && <p className="subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="actions">{actions}</div>}
    </header>
  );
}

export function Field({ label, hint, error, children, full, hintTip, className }: Children & { label: string; hint?: string; error?: string; full?: boolean; hintTip?: boolean; className?: string }) {
  const id = useId();
  const described = error || hint ? id + "-d" : undefined;
  const child = React.isValidElement(children)
    ? React.cloneElement(children as React.ReactElement<any>, { id, "aria-describedby": described, "aria-invalid": error ? true : undefined })
    : children;
  const tip = Boolean(hint && hintTip && !error);
  return (
    <div className={"field" + (full ? " full" : "") + (className ? " " + className : "")}>
      <div className="field-head">
        <label htmlFor={id}>{label}</label>
        {tip && (
          <span className="field-tip" tabIndex={0} aria-label={hint}>
            <CircleHelp aria-hidden="true" />
            <span className="field-tip-bubble" role="tooltip">{hint}</span>
          </span>
        )}
      </div>
      {child}
      {error && <span id={id + "-d"} className="err">{error}</span>}
      {hint && !error && <span id={id + "-d"} className={tip ? "sr-only" : "hint"}>{hint}</span>}
    </div>
  );
}

export function Check({ label, checked, onChange, disabled }: { label: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <label className="check">
      <Checkbox checked={checked} disabled={disabled} onCheckedChange={(v) => onChange(v === true)} />
      {label}
    </label>
  );
}

export function Badge({ children, kind, title }: Children & { kind?: "git"; title?: string }) {
  return <span className={"badge " + (kind ?? "")} title={title}>{children}</span>;
}

export type StatusKind = "good" | "warning" | "serious" | "critical" | "neutral";
const statusIcon: Record<StatusKind, string> = { good: "✓", warning: "!", serious: "▲", critical: "✕", neutral: "•" };

/** Status com cor, ícone e rótulo: nunca cor sozinha. */
export function Status({ kind, label }: { kind: StatusKind; label: string }) {
  return (
    <span className={"status " + kind}>
      <span className="dot" aria-hidden="true" />
      <span aria-hidden="true">{statusIcon[kind]}</span>
      {label}
    </span>
  );
}

export function Spinner({ label = "carregando" }: { label?: string }) {
  return <span role="status"><span className="spinner" aria-hidden="true" /> <span className="sr-only">{label}</span></span>;
}

export function Empty({ children }: Children) {
  return <div className="empty">{children}</div>;
}

export interface ErrorLike {
  message: string;
  code?: string;
  issues?: { severity: string; entity: string; id: string; field?: string; message: string }[];
}

export function ErrorBox({ error, title }: { error?: ErrorLike | null; title?: string }) {
  if (!error) return null;
  const errs = error.issues?.filter((i) => i.severity === "error") ?? [];
  const warns = error.issues?.filter((i) => i.severity !== "error") ?? [];
  return (
    <div className="alert-box" role="alert">
      <strong>{title ?? "Não foi possível concluir"}</strong>: {error.message}
      {errs.length > 0 && <ul>{errs.map((i, n) => <li key={n}>{i.entity} {i.id}{i.field ? ` (${i.field})` : ""}: {i.message}</li>)}</ul>}
      {warns.length > 0 && <ul>{warns.map((i, n) => <li key={n}>Aviso: {i.message}</li>)}</ul>}
    </div>
  );
}

export function Warnings({ issues }: { issues?: ErrorLike["issues"] }) {
  const w = issues?.filter((i) => i.severity === "warning") ?? [];
  if (!w.length) return null;
  return <div className="alert-box warn" role="status"><strong>Avisos</strong><ul>{w.map((i, n) => <li key={n}>{i.message}</li>)}</ul></div>;
}

export function KV({ items }: { items: [string, React.ReactNode][] }) {
  return (
    <dl className="kv">
      {items.map(([k, v]) => (
        <React.Fragment key={k}><dt>{k}</dt><dd>{v ?? <span className="muted">—</span>}</dd></React.Fragment>
      ))}
    </dl>
  );
}

export function Stat({ value, label, hint, className, icon }: { value: React.ReactNode; label: string; hint?: React.ReactNode; className?: string; icon?: React.ReactNode }) {
  return (
    <div className={"stat" + (className ? " " + className : "")}>
      <span className="l">{label}{icon && <span className="stat-icon">{icon}</span>}</span>
      <span className="v">{value}</span>
      {hint && <span className="small muted">{hint}</span>}
    </div>
  );
}

export function Meter({ value, label }: { value: number; label: string }) {
  const kind = value >= 100 ? "critical" : value >= 80 ? "warning" : "";
  return (
    <div role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(value)} aria-label={label} className={"meter " + kind}>
      <span style={{ width: Math.min(100, Math.max(0, value)) + "%" }} />
    </div>
  );
}

export function CopyButton({ text, label = "Copiar" }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button small onClick={async () => { await navigator.clipboard?.writeText(text); setDone(true); setTimeout(() => setDone(false), 2000); }}>
      {done ? "Copiado" : label}
    </Button>
  );
}

// ----- tabs -----

export function Tabs({ tabs, active, onChange, label }: { tabs: { id: string; label: React.ReactNode }[]; active: string; onChange: (id: string) => void; label: string }) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const onKey = (e: React.KeyboardEvent, i: number) => {
    const n = tabs.length;
    let next = -1;
    if (e.key === "ArrowRight") next = (i + 1) % n;
    if (e.key === "ArrowLeft") next = (i - 1 + n) % n;
    if (e.key === "Home") next = 0;
    if (e.key === "End") next = n - 1;
    if (next >= 0) {
      e.preventDefault();
      onChange(tabs[next].id);
      refs.current[next]?.focus();
    }
  };
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {tabs.map((t, i) => (
        <button key={t.id} ref={(el) => { refs.current[i] = el; }} role="tab" type="button" aria-selected={t.id === active} tabIndex={t.id === active ? 0 : -1}
          onClick={() => onChange(t.id)} onKeyDown={(e) => onKey(e, i)}>{t.label}</button>
      ))}
    </div>
  );
}

// ----- diálogos -----

/** Confirmação digitada com prévia do impacto (RF-ADM-SEG-04). */
export function ConfirmDialog({ open, title, resource, impact, confirmLabel, danger, onConfirm, onClose, children, busy }: Children & {
  open: boolean; title: string; resource: string; impact: React.ReactNode; confirmLabel: string; danger?: boolean;
  onConfirm: (typed: string) => void; onClose: () => void; busy?: boolean;
}) {
  const [typed, setTyped] = useState("");
  useEffect(() => { if (open) setTyped(""); }, [open]);
  return (
    <Dialog open={open} title={title} onClose={onClose}
      footer={<>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant={danger ? "danger" : "primary"} disabled={typed !== resource} loading={busy} onClick={() => onConfirm(typed)}>{confirmLabel}</Button>
      </>}>
      <div className="stack">
        <div className="alert-box warn"><strong>Impacto:</strong> {impact}</div>
        {children}
        <Field label={`Digite "${resource}" para confirmar`}>
          <input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false} />
        </Field>
      </div>
    </Dialog>
  );
}

// ----- toasts -----

type Toast = { id: number; text: string; kind?: "error" | "good" };
const ToastCtx = createContext<(text: string, kind?: Toast["kind"]) => void>(() => {});

export function ToastProvider({ children }: Children) {
  const [items, setItems] = useState<Toast[]>([]);
  const push = useCallback((text: string, kind?: Toast["kind"]) => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs, { id, text, kind }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), kind === "error" ? 9000 : 5000);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" aria-live="polite" role="status">
        {items.map((t) => <div key={t.id} className={"toast " + (t.kind ?? "")}>{t.text}</div>)}
      </div>
    </ToastCtx.Provider>
  );
}

export const useToast = () => useContext(ToastCtx);

// ----- tabela -----

export interface Column<T> {
  key: string;
  header: React.ReactNode;
  render: (row: T) => React.ReactNode;
  num?: boolean;
}

export function Table<T>({ rows, columns, caption, empty, rowKey, onRowClick, selected }: {
  rows: T[]; columns: Column<T>[]; caption?: string; empty?: React.ReactNode; rowKey: (r: T) => string; onRowClick?: (r: T) => void; selected?: string;
}) {
  if (!rows.length) return <Empty>{empty ?? "Nada por aqui."}</Empty>;
  return (
    <div className="table-wrap">
      <table className="t">
        {caption && <caption>{caption}</caption>}
        <thead><tr>{columns.map((c) => <th key={c.key} scope="col" className={c.num ? "num" : ""}>{c.header}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => {
            const k = rowKey(r);
            return (
              <tr key={k} data-selected={selected === k} onClick={onRowClick ? () => onRowClick(r) : undefined}
                style={onRowClick ? { cursor: "pointer" } : undefined}>
                {columns.map((c, i) => (
                  <td key={c.key} className={c.num ? "num" : ""}>
                    {i === 0 && onRowClick ? <button type="button" className="btn ghost small" style={{ padding: 0 }} onClick={(e) => { e.stopPropagation(); onRowClick(r); }}>{c.render(r)}</button> : c.render(r)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function Pager({ total, offset, limit, onChange }: { total: number; offset: number; limit: number; onChange: (offset: number) => void }) {
  if (total <= limit) return null;
  const page = Math.floor(offset / limit) + 1, pages = Math.ceil(total / limit);
  return (
    <nav className="row" aria-label="Paginação" style={{ marginTop: 8 }}>
      <Button small disabled={offset === 0} onClick={() => onChange(Math.max(0, offset - limit))}>Anterior</Button>
      <span className="small muted">página {page} de {pages} · {total} itens</span>
      <Button small disabled={offset + limit >= total} onClick={() => onChange(offset + limit)}>Próxima</Button>
    </nav>
  );
}

// ----- árvore -----

export interface TreeNode {
  id: string;
  label: React.ReactNode;
  children?: TreeNode[];
  meta?: React.ReactNode;
}

/** Árvore com navegação por teclado (setas, Home, End, Enter). */
export function Tree({ nodes, selected, onSelect, label }: { nodes: TreeNode[]; selected?: string; onSelect: (id: string) => void; label: string }) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const flat = useMemo(() => {
    const out: { node: TreeNode; depth: number; parent?: string }[] = [];
    const walk = (ns: TreeNode[], depth: number, parent?: string) => ns.forEach((n) => {
      out.push({ node: n, depth, parent });
      if (n.children?.length && open[n.id] !== false) walk(n.children, depth + 1, n.id);
    });
    walk(nodes, 0);
    return out;
  }, [nodes, open]);
  const focusAt = (i: number) => (document.querySelector(`[data-tree-index="${i}"]`) as HTMLElement | null)?.focus();
  const onKey = (e: React.KeyboardEvent, i: number) => {
    const it = flat[i];
    const has = !!it.node.children?.length;
    if (e.key === "ArrowDown") { e.preventDefault(); focusAt(Math.min(flat.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); focusAt(Math.max(0, i - 1)); }
    else if (e.key === "ArrowRight" && has) { e.preventDefault(); setOpen((o) => ({ ...o, [it.node.id]: true })); }
    else if (e.key === "ArrowLeft") {
      e.preventDefault();
      if (has && open[it.node.id] !== false) setOpen((o) => ({ ...o, [it.node.id]: false }));
      else if (it.parent) focusAt(flat.findIndex((x) => x.node.id === it.parent));
    } else if (e.key === "Home") { e.preventDefault(); focusAt(0); }
    else if (e.key === "End") { e.preventDefault(); focusAt(flat.length - 1); }
    else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(it.node.id); }
  };
  const render = (ns: TreeNode[]): React.ReactNode => ns.map((n) => {
    const i = flat.findIndex((x) => x.node.id === n.id);
    const has = !!n.children?.length;
    const expanded = has ? open[n.id] !== false : undefined;
    return (
      <li key={n.id} role="treeitem" aria-selected={selected === n.id} aria-expanded={expanded}>
        <div className="node" tabIndex={selected === n.id || (!selected && i === 0) ? 0 : -1} data-tree-index={i}
          onClick={() => onSelect(n.id)} onKeyDown={(e) => onKey(e, i)}>
          <span className="toggle" aria-hidden="true" onClick={(e) => { if (has) { e.stopPropagation(); setOpen((o) => ({ ...o, [n.id]: !expanded })); } }}>
            {has ? (expanded ? "▾" : "▸") : ""}
          </span>
          {n.label}
          {n.meta && <span className="small muted" style={{ marginLeft: "auto" }}>{n.meta}</span>}
        </div>
        {has && expanded && <ul role="group">{render(n.children!)}</ul>}
      </li>
    );
  });
  return <ul className="tree" role="tree" aria-label={label}>{render(nodes)}</ul>;
}

// ----- diff -----

/** Diff lado a lado por linhas (LCS), para versões de alias e de política (RF-ADM-ALI-02). */
export function DiffView({ left, right, leftLabel, rightLabel }: { left: string; right: string; leftLabel: string; rightLabel: string }) {
  const a = left.split("\n"), b = right.split("\n");
  const n = a.length, m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const rows: [string | null, string | null, string][] = [];
  let i = 0, j = 0;
  while (i < n || j < m) {
    if (i < n && j < m && a[i] === b[j]) { rows.push([a[i], b[j], ""]); i++; j++; }
    else if (j < m && (i >= n || dp[i][j + 1] >= dp[i + 1][j])) { rows.push([null, b[j], "add"]); j++; }
    else { rows.push([a[i], null, "del"]); i++; }
  }
  return (
    <div className="diff" role="table" aria-label={`Diferença entre ${leftLabel} e ${rightLabel}`}>
      <div className="head" role="columnheader">{leftLabel}</div><div className="head" role="columnheader">{rightLabel}</div>
      {rows.map(([l, r, k], idx) => (
        <React.Fragment key={idx}>
          <div className={k === "del" ? "del" : ""} role="cell">{k === "del" ? "− " : "  "}{l ?? ""}</div>
          <div className={k === "add" ? "add" : ""} role="cell">{k === "add" ? "+ " : "  "}{r ?? ""}</div>
        </React.Fragment>
      ))}
    </div>
  );
}

// ----- gráficos -----

export interface Series {
  name: string;
  color: string; // var(--series-n)
  points: { t: number; v: number }[];
}

const fmtTime = (t: number) => new Date(t).toLocaleString("pt-BR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" });

/** Linhas com crosshair e tooltip, legenda e alternância para tabela (skill dataviz). */
export function LineChart({ series, title, unit = "", format = (v: number) => v.toLocaleString("pt-BR", { maximumFractionDigits: 2 }), height = 200, target }: {
  series: Series[]; title: string; unit?: string; format?: (v: number) => string; height?: number; target?: { v: number; label: string };
}) {
  const [hover, setHover] = useState<number | null>(null);
  const [table, setTable] = useState(false);
  const width = 640, pad = { l: 48, r: 12, t: 10, b: 24 };
  // A margem esquerda acompanha o rótulo mais longo do eixo (ex.: "US$ 0,000868"), sem vazar do cartão.
  const ts = Array.from(new Set(series.flatMap((s) => s.points.map((p) => p.t)))).sort((x, y) => x - y);
  const maxV = Math.max(1e-9, target?.v ?? 0, ...series.flatMap((s) => s.points.map((p) => p.v)));
  const minT = ts[0] ?? 0, maxT = ts[ts.length - 1] ?? 1;
  const x = (t: number) => pad.l + ((t - minT) / Math.max(1, maxT - minT)) * (width - pad.l - pad.r);
  const y = (v: number) => pad.t + (1 - v / (maxV * 1.1)) * (height - pad.t - pad.b);
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * maxV * 1.1);
  pad.l = Math.max(48, Math.max(...ticks.map((v) => format(v).length)) * 6 + 10);
  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * width;
    let best = 0;
    ts.forEach((t, i) => { if (Math.abs(x(t) - px) < Math.abs(x(ts[best]) - px)) best = i; });
    setHover(ts.length ? best : null);
  };
  if (!ts.length) return <Empty>Sem dados no período.</Empty>;
  const ht = hover !== null ? ts[hover] : null;
  return (
    <figure className="chart" style={{ margin: 0 }}>
      <div className="row" style={{ marginBottom: 4 }}>
        <figcaption><h3 style={{ margin: 0 }}>{title}</h3></figcaption>
        <Button small variant="ghost" onClick={() => setTable(!table)} aria-pressed={table} style={{ marginLeft: "auto" }}>{table ? "Ver gráfico" : "Ver tabela"}</Button>
      </div>
      {series.length > 1 && (
        <div className="legend">{series.map((s) => <span key={s.name}><span className="sw" style={{ background: s.color }} />{s.name}</span>)}</div>
      )}
      {table ? (
        <div className="table-wrap" style={{ maxHeight: height + 40, overflow: "auto" }}>
          <table className="t"><caption>{title}</caption>
            <thead><tr><th scope="col">Horário</th>{series.map((s) => <th key={s.name} scope="col" className="num">{s.name}</th>)}</tr></thead>
            <tbody>{ts.map((t) => <tr key={t}><td>{fmtTime(t)}</td>{series.map((s) => <td key={s.name} className="num">{format(s.points.find((p) => p.t === t)?.v ?? 0)}{unit}</td>)}</tr>)}</tbody>
          </table>
        </div>
      ) : (
        <>
          <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={title} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
            {ticks.map((v, i) => (
              <g key={i}>
                <line x1={pad.l} x2={width - pad.r} y1={y(v)} y2={y(v)} stroke="var(--grid)" strokeWidth={1} />
                <text x={pad.l - 6} y={y(v) + 4} textAnchor="end" fontSize={10} fill="var(--text-muted)">{format(v)}</text>
              </g>
            ))}
            <text x={pad.l} y={height - 6} fontSize={10} fill="var(--text-muted)">{fmtTime(minT)}</text>
            <text x={width - pad.r} y={height - 6} fontSize={10} fill="var(--text-muted)" textAnchor="end">{fmtTime(maxT)}</text>
            {target && (
              <g>
                <line x1={pad.l} x2={width - pad.r} y1={y(target.v)} y2={y(target.v)} stroke="var(--text-secondary)" strokeDasharray="4 4" strokeWidth={1} />
                <text x={width - pad.r} y={y(target.v) - 4} textAnchor="end" fontSize={10} fill="var(--text-secondary)">{target.label}</text>
              </g>
            )}
            {series.map((s) => s.points.length === 1 ? (
              // Uma medição só não forma linha: desenha o ponto.
              <circle key={s.name} cx={x(s.points[0].t)} cy={y(s.points[0].v)} r={4} fill={s.color} />
            ) : (
              <polyline key={s.name} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round"
                points={s.points.map((p) => `${x(p.t)},${y(p.v)}`).join(" ")} />
            ))}
            {ht !== null && <line x1={x(ht)} x2={x(ht)} y1={pad.t} y2={height - pad.b} stroke="var(--border-strong)" strokeWidth={1} />}
            {ht !== null && series.map((s) => {
              const p = s.points.find((q) => q.t === ht);
              return p ? <circle key={s.name} cx={x(p.t)} cy={y(p.v)} r={4} fill={s.color} stroke="var(--surface-1)" strokeWidth={2} /> : null;
            })}
          </svg>
          {ht !== null && (
            <div className="tooltip" style={{ left: `${(x(ht) / width) * 100}%`, top: 8, transform: x(ht) > width / 2 ? "translateX(-105%)" : "translateX(8px)" }}>
              <div className="muted">{fmtTime(ht)}</div>
              {series.map((s) => {
                const p = s.points.find((q) => q.t === ht);
                return <div key={s.name}><span className="sw" style={{ background: s.color, display: "inline-block", width: 10, height: 3, marginRight: 4 }} />{s.name}: <strong>{p ? format(p.v) + unit : "—"}</strong></div>;
              })}
            </div>
          )}
        </>
      )}
    </figure>
  );
}

/** Barras horizontais com tooltip por barra (custos por grupo, magnitude). */
export function BarChart({ data, title, format = (v: number) => v.toLocaleString("pt-BR", { maximumFractionDigits: 2 }), max = 12 }: {
  data: { label: string; value: number }[]; title: string; format?: (v: number) => string; max?: number;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const rows = data.slice(0, max);
  const top = Math.max(1e-9, ...rows.map((r) => r.value));
  if (!rows.length) return <Empty>Sem dados no período.</Empty>;
  const rowH = 26, width = 640, labelW = 180;
  return (
    <figure className="chart" style={{ margin: 0 }}>
      <figcaption><h3>{title}</h3></figcaption>
      <svg viewBox={`0 0 ${width} ${rows.length * rowH}`} role="img" aria-label={title}>
        {rows.map((r, i) => {
          const w = Math.max(2, ((width - labelW - 80) * r.value) / top);
          return (
            <g key={r.label} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
              <rect x={0} y={i * rowH} width={width} height={rowH} fill="transparent" />
              <text x={labelW - 8} y={i * rowH + 17} textAnchor="end" fontSize={11} fill="var(--text-secondary)">{r.label.length > 26 ? r.label.slice(0, 25) + "…" : r.label}</text>
              <rect x={labelW} y={i * rowH + 6} width={w} height={rowH - 12} rx={4} fill="var(--series-1)" opacity={hover === null || hover === i ? 1 : 0.55} />
              <text x={labelW + w + 6} y={i * rowH + 17} fontSize={11} fill="var(--text-primary)">{format(r.value)}</text>
            </g>
          );
        })}
      </svg>
      {hover !== null && <div className="tooltip" style={{ left: labelW, top: hover * rowH - 30 }}>{rows[hover].label}: <strong>{format(rows[hover].value)}</strong></div>}
    </figure>
  );
}
